import React from 'react';
import {
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Flex,
  Spinner,
  Alert,
  Button,
  Center,
  useDisclosure,
  Link,
} from '@chakra-ui/react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import {format} from 'date-fns';
import {useGetCampaignChats} from '../../hooks/api/chatabm';
import {GetCampaignChatsResponse} from '../../shared/api/definitions/chatabm';
import {CreateCampaignChatModal} from './CreateCampaignChatModal';
import {Link as RouterLink} from 'react-router-dom';

const columnHelper = createColumnHelper<GetCampaignChatsResponse>();
const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: (info) => (
      <Link
        as={RouterLink}
        color="kblue.300"
        display="inline-block"
        to={`/chats/${info.row.original._id.toString()}`}
      >
        <Text>{info.getValue()}</Text>
      </Link>
    ),
  }),
  columnHelper.accessor('createdAt', {
    header: 'Created',
    cell: (info) => {
      const createdAt = info.getValue();
      return (
        <Text color="kgray.500" fontSize="sm">
          {format(createdAt, 'MMM d, yyyy h:mm a')}
        </Text>
      );
    },
  }),
];

export const CampaignChatsList = () => {
  const {data: campaignChats, isLoading, error} = useGetCampaignChats({});
  const createCampaignChatModal = useDisclosure();

  const table = useReactTable({
    data: campaignChats ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) {
    return (
      <Flex justify="center" align="center" p={8}>
        <Spinner size="lg" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        Unable to load campaign chats. Please try again.
      </Alert>
    );
  }

  return (
    <Center>
      <Flex direction="column" gap={8} p={4} maxW="800px" w="100%">
        <Flex align="baseline" justify="space-between" w="100%">
          <Text fontSize="lg" fontWeight="500">
            Campaign Chats
          </Text>
          <Button
            colorScheme="kbuttonblue"
            onClick={createCampaignChatModal.onOpen}
          >
            Create Campaign Chat
          </Button>
          <CreateCampaignChatModal
            isOpen={createCampaignChatModal.isOpen}
            onClose={createCampaignChatModal.onClose}
          />
        </Flex>

        <TableContainer
          border="1px"
          borderColor="kgray.200"
          borderRadius="md"
          overflow="hidden"
          w="100%"
        >
          <Table variant="simple">
            <Thead bg="kgray.100">
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th key={header.id} fontWeight="500">
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table.getRowModel().rows.map((row) => (
                <Tr key={row.id} _hover={{bg: 'kgray.100'}}>
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>

        {(!campaignChats || campaignChats.length === 0) && (
          <Box textAlign="center" color="kgray.400">
            No campaign chats found.
          </Box>
        )}
      </Flex>
    </Center>
  );
};
